import { ConfirmDialog } from "@/components/ConfirmDialog";
import { InlineError, LoadingIndicator } from "@/components/QueryState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { accountUsageLabel } from "@/lib/account-usage";
import {
  api,
  type AppInfo,
  type CategoryInfo,
  type HubAccountWithBindings,
  type Permissions,
} from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { buildCategoryTree } from "@/lib/hub-catalog";
import { useCatalog, useCategories } from "@/lib/use-hub-catalog";
import { useConnectReturn } from "@/hooks/useConnectReturn";
import {
  HUB_ACCOUNTS_KEY,
  connectedForBoo,
  hubAccountsKey,
  partitionForBoo,
  usableByBoo,
} from "@/lib/hub-accounts";
import { useBooConfigurable } from "@/lib/use-boo-configurable";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import {
  Check,
  ChevronDown,
  ChevronRight,
  Loader2,
  MoreHorizontal,
  Pencil,
  SlidersHorizontal,
  Users,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useDebounced } from "@/hooks/useDebounced";
import { CONNECTORS_SUBCOPY, CONNECT_TARGET, connectedByApp, useConnectApp } from "./hub-lib";
import { ConnectorGrid, ConnectorsWaitingNotice } from "./connectors/ConnectorTiles";
import { useConnectors } from "@/lib/use-connectors";
import { ConnectionTile } from "../connections/ConnectionParts";
import { connectionForHubSlug, ms365StatusKey } from "../connections/connections-lib";
import type { ConnectionStatus } from "@/lib/connection-status";

// Integrations browser backed by the Boo Integrations Hub. Accounts +
// credentials live in the hub under the org's partition; boo-backend owns
// each account's scope and *bindings* — where it may be used. Two modes:
//   - containerId set (Boo's Integrations tab): the Boo's own private
//     connections plus what the workspace shares. Connect here is for this
//     Boo: the account is private to it until a Boo admin shares it with the
//     workspace.
//   - containerId absent (org settings): workspace accounts only — connected
//     at org level or shared by a Boo. Private Boo connections are not listed
//     here. Connect binds org-wide (every Boo); narrowing happens on the
//     manage page.
// Connected accounts live behind a synthetic "Connected" entry in the
// category rail rather than a section above the catalog — an org with a
// hundred accounts would otherwise push the catalog off-screen every time
// someone comes to connect one more.
// A tile click leaves for the hub's hosted connect page (a one-time link from
// boo-backend; the browser holds no hub credential) and comes back here with
// the outcome; boo-backend has bound the account for this view's scope by
// then. Purpose and scope are edited afterwards on the account row / manage
// page.
// Hub entries with `connectedBy: "app"` are Connections boo-backend makes
// itself (Microsoft 365): on the Boo tab their tiles open the connection's page
// instead of a hub connect flow; org settings asks the hub for hub-connected
// apps only. The code hosts aren't in the catalog at all, so a search for them
// points to Connections.

// Mirrors PURPOSE_MAX in boo-backend routes/integrations.ts.
const PURPOSE_MAX = 80;

// What the right pane shows: the synthetic Connected view, the connectors Boo
// built (Custom), the whole catalog, or one hub category.
// "connected"/"custom"/"all" are ours; only category slugs ever reach the
// hub. Exported (with the rail + menu below) for the onboarding catalog pane.
export type BrowserView =
  | { kind: "connected" }
  | { kind: "custom" }
  | { kind: "all" }
  | { kind: "category"; slug: string };

function monogram(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

// "google_sheets" → "Google Sheets" for rows where we only have the slug.
function appName(slug: string): string {
  return slug
    .split(/[_-]/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

function accountDisplayName(account: HubAccountWithBindings): string {
  return account.appName ?? appName(account.app);
}

// The two lists the Boo tab shows; the org tab has only `shared`.
interface Lists {
  own: HubAccountWithBindings[];
  shared: HubAccountWithBindings[];
}

export function HubApps({
  containerId,
  permissions,
}: {
  containerId?: string;
  permissions?: Permissions;
}) {
  const queryClient = useQueryClient();
  useConnectReturn();

  const accountsQuery = useQuery({
    queryKey: hubAccountsKey(containerId),
    queryFn: () => api.integrations.accounts(containerId),
  });

  // Boo tab: the Boo's own permissions (admins connect for their Boo, no org
  // role needed). Org tab: the org-level gate from the accounts response.
  const canManage = containerId
    ? permissions?.canManageIntegrations === true
    : accountsQuery.data?._permissions?.canManage === true;
  const canView = !containerId || permissions?.canViewIntegrations === true || canManage;

  if (!canView) return null;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: HUB_ACCOUNTS_KEY });

  const accounts = accountsQuery.data?.accounts ?? [];
  // Boo tab: own private connections, then the workspace pool. Admins see the
  // whole pool (usable ones first) so they can pull one in; members see only
  // what this Boo can already use. Org tab: the workspace pool as-is.
  const lists: Lists = (() => {
    if (!containerId) return { own: [], shared: accounts };
    const { own, shared } = partitionForBoo(accounts, containerId);
    const usable = (a: HubAccountWithBindings) => usableByBoo(a, containerId);
    return {
      own,
      shared: canManage
        ? [...shared].sort((a, b) => Number(usable(b)) - Number(usable(a)))
        : shared.filter(usable),
    };
  })();

  if (accountsQuery.isLoading) return <LoadingIndicator />;

  // Read-only users get just their connected list, then the Boo's connectors
  // — the catalog and its rail are connect surfaces they can't use.
  if (!canManage) {
    return (
      <section className="space-y-3">
        <div>
          <h2 className="text-base font-semibold">Connected</h2>
          <p className="text-sm text-muted-foreground max-w-2xl">
            {connectedSubcopy(containerId)} You can see connected integrations but not change them.
          </p>
        </div>
        {accountsQuery.isError ? (
          <InlineError error={accountsQuery.error} />
        ) : (
          <ConnectedLists
            lists={lists}
            containerId={containerId}
            canManage={false}
            onChanged={invalidate}
          />
        )}
        <ReadOnlyConnectors containerId={containerId} />
      </section>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {accountsQuery.isError && <InlineError error={accountsQuery.error} />}
      <CatalogBrowser
        containerId={containerId}
        lists={lists}
        onAccountsChanged={invalidate}
      />
    </div>
  );
}

function ReadOnlyConnectors({ containerId }: { containerId?: string }) {
  const { orgId } = useParams({ strict: false }) as { orgId?: string };
  const { connectors, error } = useConnectors(containerId);
  if (error) return <InlineError error={error} />;
  if (!containerId || !orgId || connectors.length === 0) return null;
  return (
    <div className="space-y-3 pt-5">
      <div>
        <h2 className="text-base font-semibold">Custom integrations</h2>
        <p className="text-sm text-muted-foreground max-w-2xl">{CONNECTORS_SUBCOPY}</p>
      </div>
      <ConnectorGrid connectors={connectors} orgId={orgId} containerId={containerId} />
    </div>
  );
}

function connectedSubcopy(containerId?: string): string {
  return containerId
    ? "Connections made from this Boo stay private to it unless its admins share them. Connections the workspace shares are listed below them."
    : "Connections made for the whole workspace, and which Boos use them. Connections made inside a Boo stay private to it unless its admins share them.";
}

// The connected accounts, split for the Boo tab into the Boo's own private
// connections and what the workspace shares; the org tab is one list.
function ConnectedLists({
  lists,
  containerId,
  canManage,
  onChanged,
}: {
  lists: Lists;
  containerId?: string;
  canManage: boolean;
  onChanged: () => void;
}) {
  const { own, shared } = lists;
  const rows = (list: HubAccountWithBindings[]) => (
    <div className="grid items-start gap-3 xl:grid-cols-2">
      {list.map((account) => (
        <AccountRow
          key={account.id}
          account={account}
          containerId={containerId}
          canManage={canManage}
          onChanged={onChanged}
        />
      ))}
    </div>
  );

  if (own.length === 0 && shared.length === 0) {
    return (
      <div className="rounded-lg border p-6 text-center text-sm text-muted-foreground">
        No integrations are available yet.
      </div>
    );
  }

  if (!containerId) return rows(shared);

  return (
    <div className="space-y-5">
      <section className="space-y-2">
        <h3 className="text-sm font-medium">This Boo’s connections</h3>
        {own.length === 0 ? (
          <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
            Nothing connected from this Boo yet.
          </p>
        ) : (
          rows(own)
        )}
      </section>
      {shared.length > 0 && (
        <section className="space-y-2">
          <div className="flex items-center gap-1.5">
            <Users className="size-3.5 text-muted-foreground" />
            <h3 className="text-sm font-medium">Shared with the workspace</h3>
          </div>
          {rows(shared)}
        </section>
      )}
    </div>
  );
}

function AccountRow({
  account,
  containerId,
  canManage,
  onChanged,
}: {
  account: HubAccountWithBindings;
  containerId?: string;
  // Viewer may act in this context: Boo configure on the Boo tab, org
  // owner/admin on the org tab. Per-account actions also need account.canManage.
  canManage: boolean;
  onChanged: () => void;
}) {
  const { orgId } = useParams({ strict: false }) as { orgId?: string };
  const { names } = useBooConfigurable();
  const orgBinding = account.bindings.find((b) => b.containerId === null);
  const orgWide = orgBinding !== undefined;
  const booBinding = containerId
    ? account.bindings.find((b) => b.containerId === containerId)
    : undefined;
  const [confirmUnshare, setConfirmUnshare] = useState(false);

  const displayName = accountDisplayName(account);
  // The binding whose purpose is shown: the Boo's own binding first, else the
  // org-wide one, else whatever exists. Access changes happen on the manage page.
  const primaryBinding = booBinding ?? orgBinding ?? account.bindings[0];
  const isShared = account.scope === "workspace";
  // Boo tab only: is this account usable by the Boo being viewed?
  const availableHere = containerId !== undefined && (orgWide || booBinding !== undefined);
  // A shared account this Boo isn't using yet is the one case to pull in.
  const canUseHere = containerId !== undefined && canManage && isShared && !availableHere;
  // Dim only the identity when unavailable — never the actions (a child of
  // an opacity group can't be un-dimmed, so the row itself must stay full).
  const dim = containerId !== undefined && !availableHere;
  const sharedFrom =
    !containerId && account.homeContainerId
      ? (names.get(account.homeContainerId) ?? "a Boo")
      : null;

  const fail = (fallback: string) => (err: unknown) =>
    toast.error(err instanceof Error ? err.message : fallback);
  const useHere = useMutation({
    mutationFn: () =>
      api.integrations.bind({
        hubAccountId: account.id,
        containerId: containerId!,
        toolAccess: primaryBinding?.toolAccess,
        purpose: primaryBinding?.purpose ?? undefined,
      }),
    onSuccess: onChanged,
    // 403 account_private carries the backend's own copy.
    onError: fail("Could not add to this Boo"),
  });
  const stopUsing = useMutation({
    mutationFn: () => api.integrations.unbind(booBinding!.id),
    onSuccess: onChanged,
    onError: fail("Could not remove from this Boo"),
  });
  const share = useMutation({
    mutationFn: () => api.integrations.share(account.id),
    onSuccess: () => {
      toast.success(`${displayName} shared with the workspace`);
      onChanged();
    },
    onError: fail("Could not share"),
  });
  const unshare = useMutation({
    mutationFn: () => api.integrations.unshare(account.id),
    onSuccess: () => {
      setConfirmUnshare(false);
      toast.success(
        `${displayName} is private to ${names.get(account.homeContainerId ?? "") ?? "the Boo it was connected from"} again`,
      );
      onChanged();
    },
    onError: fail("Could not stop sharing"),
  });
  const busy = useHere.isPending || stopUsing.isPending || share.isPending || unshare.isPending;

  // Row menu: share/stop sharing need account.canShare (configure on the home
  // Boo); removing this Boo's own binding needs only the Boo. The org tab has
  // no per-Boo binding to remove. Stop sharing is offered only while the home
  // Boo is still around (in the viewer's Boo list).
  const homeKnown = account.homeContainerId !== null && names.has(account.homeContainerId);
  const menuItems: { label: string; onClick: () => void; destructive?: boolean }[] = [];
  if (account.canShare && !isShared) {
    menuItems.push({ label: "Share with the whole workspace", onClick: () => share.mutate() });
  }
  if (account.canShare && isShared && homeKnown) {
    menuItems.push({ label: "Stop sharing", onClick: () => setConfirmUnshare(true) });
  }
  if (containerId && isShared && booBinding && !orgWide) {
    menuItems.push({ label: "Remove from this Boo", onClick: () => stopUsing.mutate(), destructive: true });
  }

  return (
    <div className="flex items-center gap-3 rounded-lg border bg-background p-3">
      {account.logoUrl ? (
        <img
          src={account.logoUrl}
          alt=""
          className={cn("size-8 shrink-0 rounded object-contain", dim && "opacity-60")}
        />
      ) : (
        <div
          className={cn(
            "flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-xs font-semibold",
            dim && "opacity-60",
          )}
        >
          {monogram(displayName)}
        </div>
      )}
      <div className={cn("min-w-0 flex-1", dim && "opacity-60")}>
        <p className="truncate text-sm font-medium leading-tight">{displayName}</p>
        <p className="truncate text-xs text-muted-foreground">
          {account.label}
          {sharedFrom && <span className="text-muted-foreground/80"> · Shared from {sharedFrom}</span>}
        </p>
        {primaryBinding && (
          <PurposeLine
            binding={primaryBinding}
            canManage={canManage && (account.canManage || booBinding !== undefined)}
            onChanged={onChanged}
          />
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {!account.healthy && <Badge variant="destructive">Needs reconnect</Badge>}
        {containerId ? (
          // Boo tab: the Boo's own connections are unbadged (the common case);
          // workspace ones say whether this Boo is using them.
          isShared &&
          (availableHere ? (
            <Badge variant="secondary" className="gap-1 text-muted-foreground">
              <Check className="size-3" />
              Available here
            </Badge>
          ) : (
            !canUseHere && (
              <Badge variant="outline" className="text-muted-foreground">
                Not available to this Boo
              </Badge>
            )
          ))
        ) : (
          <Badge variant="secondary" className="tabular-nums">
            {accountUsageLabel(account.bindings)}
          </Badge>
        )}
        {canUseHere ? (
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            disabled={busy}
            onClick={() => useHere.mutate()}
          >
            {useHere.isPending && <Loader2 className="size-3.5 animate-spin" />}
            Use in this Boo
          </Button>
        ) : (
          canManage &&
          orgId &&
          (account.canManage || account.canShare || account.canDisconnect || booBinding !== undefined) && (
            <Button asChild size="sm" variant="outline" className="gap-1.5">
              <Link
                to="/orgs/$orgId/integrations/$accountId"
                params={{ orgId, accountId: account.id }}
                search={{ tab: "tools", boo: containerId }}
              >
                <SlidersHorizontal className="size-3.5" />
                Manage
              </Link>
            </Button>
          )
        )}
        {canManage && menuItems.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm" aria-label="Connection actions" disabled={busy}>
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {menuItems.map((item) => (
                <DropdownMenuItem
                  key={item.label}
                  className={item.destructive ? "text-destructive" : undefined}
                  onClick={item.onClick}
                >
                  {item.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <ConfirmDialog
        open={confirmUnshare}
        onOpenChange={setConfirmUnshare}
        title={`Stop sharing ${displayName}?`}
        description="It becomes private to the Boo it was connected from. Every other Boo loses this account."
        confirmLabel="Stop sharing"
        loading={unshare.isPending}
        onConfirm={() => unshare.mutate()}
      />
    </div>
  );
}

// One-line purpose display with inline editing — the note Boos see when
// picking between same-app accounts ("support inbox" vs an opaque id).
function PurposeLine({
  binding,
  canManage,
  onChanged,
}: {
  binding: { id: string; purpose: string | null };
  canManage: boolean;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const save = useMutation({
    mutationFn: (purpose: string | null) => api.integrations.updateBinding(binding.id, { purpose }),
    onSuccess: () => {
      setEditing(false);
      onChanged();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not save"),
  });

  if (editing) {
    return (
      <Input
        autoFocus
        value={draft}
        maxLength={PURPOSE_MAX}
        placeholder={'What’s this account for? e.g. "support inbox"'}
        className="mt-1 h-7 text-xs"
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") save.mutate(draft.trim() || null);
          if (e.key === "Escape") setEditing(false);
        }}
        onBlur={() => setEditing(false)}
        disabled={save.isPending}
      />
    );
  }

  if (!binding.purpose && !canManage) return null;
  return (
    <button
      type="button"
      disabled={!canManage}
      onClick={() => {
        setDraft(binding.purpose ?? "");
        setEditing(true);
      }}
      className="group mt-0.5 inline-flex max-w-full items-center gap-1 text-left text-xs text-muted-foreground disabled:cursor-default"
    >
      <span className="truncate italic">{binding.purpose ?? "Add a note for your Boos…"}</span>
      {canManage && (
        <Pencil className="size-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-60 group-focus-visible:opacity-60" />
      )}
    </button>
  );
}

// One page of catalog results. 24 fills the 2- and 3-column grids evenly;
// Load more appends another page.
const PAGE_SIZE = 24;

// Search words that mean the code hosts, which live under Connections.
const CODE_HOST_QUERY = /^(git|github|gitlab|repo|repos|repositor(y|ies)|code hosts?)$/i;

// The full browser: rail on the left (Connected + taxonomy, Zapier-style),
// search + content on the right. The right pane is either the connected
// accounts or the catalog grid, depending on the rail pick; one search box
// filters whichever is showing. Opens on All integrations — the catalog is
// the thing to act on; Connected is one click away and shows a connect-more
// nudge until the target is met. On small screens the rail folds into the
// dropdown next to the search box.
function CatalogBrowser({
  containerId,
  lists,
  onAccountsChanged,
}: {
  containerId?: string;
  lists: Lists;
  onAccountsChanged: () => void;
}) {
  const [query, setQuery] = useState("");
  const [view, setView] = useState<BrowserView>({ kind: "all" });
  const setPicked = setView;
  // What counts as connected for this view: the Boo's own connections plus
  // the shared ones it can use; on the org tab, the whole pool.
  const inUse = containerId ? connectedForBoo([...lists.own, ...lists.shared], containerId) : lists.shared;
  const connectedCount = inUse.length;
  const connectedByAppCounts = connectedByApp(inUse);

  // The query is debounced so the hub isn't hit per keystroke; category picks
  // are clicks, so they fetch immediately. Query and category compose —
  // searching within a category narrows it. The Connected view filters
  // client-side with the same (undebounced) text.
  const debouncedQuery = useDebounced(query.trim(), 250);
  const { apps, loading, error, hasMore, loadMore } = useCatalog({
    query: debouncedQuery,
    category: view.kind === "category" ? view.slug : undefined,
    connectedBy: containerId ? undefined : "hub",
    pageSize: PAGE_SIZE,
  });
  const { orgId } = useParams({ strict: false }) as { orgId?: string };
  const ms365 = useQuery({
    queryKey: ms365StatusKey(containerId ?? ""),
    queryFn: () => api.ms365.status(containerId!),
    enabled: !!containerId,
  });
  // An app-connected entry shows only on a Boo, and only if this app has a
  // connection page for it.
  const connectionTile = (app: AppInfo) => {
    const def = connectionForHubSlug(app.slug);
    if (!containerId || !orgId || !def) return null;
    const status: ConnectionStatus | undefined =
      def.id === "microsoft-365" && ms365.data
        ? {
            loading: false,
            state: ms365.data.connected ? "connected" : "off",
            label: ms365.data.connected ? `Connected as ${ms365.data.account ?? "the Boo account"}` : "Not connected",
          }
        : undefined;
    return <ConnectionTile key={app.slug} app={app} def={def} status={status} orgId={orgId} containerId={containerId} />;
  };
  const { connect, connecting } = useConnectApp({ containerId });
  const { categories, loading: categoriesLoading } = useCategories();
  const tree = buildCategoryTree(categories);
  const { connectors, waitingCount: connectorsWaiting, error: connectorsError } = useConnectors(containerId);

  const q = query.trim().toLowerCase();
  const matchesQuery = (a: HubAccountWithBindings) =>
    q === "" ||
    accountDisplayName(a).toLowerCase().includes(q) ||
    (a.label ?? "").toLowerCase().includes(q) ||
    a.bindings.some((b) => b.purpose?.toLowerCase().includes(q));
  const filtered: Lists = {
    own: lists.own.filter(matchesQuery),
    shared: lists.shared.filter(matchesQuery),
  };
  const anyListed = lists.own.length + lists.shared.length > 0;
  const anyMatch = filtered.own.length + filtered.shared.length > 0;

  const connectorsShown = connectors.filter((c) => q === "" || c.name.toLowerCase().includes(q));

  const heading =
    view.kind === "connected"
      ? "Connected"
      : view.kind === "custom"
        ? "Custom integrations"
        : view.kind === "category"
          ? (categories.find((c) => c.slug === view.slug)?.name ?? "All integrations")
          : "All integrations";
  const subcopy =
    view.kind === "connected"
      ? connectedSubcopy(containerId)
      : view.kind === "custom"
        ? CONNECTORS_SUBCOPY
        : containerId
          ? "Connect the tools this Boo can use. Anything you connect here is private to this Boo until you share it with the workspace."
          : "Anything you connect here is shared with the workspace: allow it for every Boo, or just the ones you pick.";

  return (
    <div className="flex items-start gap-8">
      <CategoryRail
        tree={tree}
        loading={categoriesLoading}
        connectedCount={connectedCount}
        customCount={connectors.length}
        customWaiting={connectorsWaiting > 0}
        view={view}
        onPick={setPicked}
      />

      <section className="min-w-0 flex-1 space-y-3">
        <div>
          <h2 className="text-base font-semibold">{heading}</h2>
          <p className="text-sm text-muted-foreground max-w-2xl">{subcopy}</p>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row">
          {/* Rail hidden below lg — the dropdown steps in as the filter. */}
          <div className="lg:hidden">
            <CategoryMenu connectedCount={connectedCount} customCount={connectors.length} view={view} onPick={setPicked} />
          </div>
          <Input
            placeholder={
              view.kind === "connected"
                ? "Search connected…"
                : view.kind === "custom"
                  ? "Search custom integrations…"
                  : "Search integrations…"
            }
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="sm:flex-1"
          />
        </div>

        {containerId && orgId && view.kind !== "connected" && CODE_HOST_QUERY.test(query.trim()) && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/30 px-4 py-3">
            <p className="text-sm text-muted-foreground">GitHub and GitLab are set up under Connections.</p>
            <Button asChild size="sm" variant="outline">
              <Link to="/orgs/$orgId/containers/$containerId" params={{ orgId, containerId }}>
                Open Connections
              </Link>
            </Button>
          </div>
        )}

        {connectorsError && <InlineError error={connectorsError} />}

        {view.kind !== "custom" && connectorsWaiting > 0 && (
          <ConnectorsWaitingNotice count={connectorsWaiting} onOpen={() => setPicked({ kind: "custom" })} />
        )}

        {view.kind === "custom" ? (
          connectorsShown.length === 0 ? (
            <p className="p-2 text-sm text-muted-foreground">No custom integrations match your search.</p>
          ) : (
            orgId && containerId && <ConnectorGrid connectors={connectorsShown} orgId={orgId} containerId={containerId} />
          )
        ) : view.kind === "connected" ? (
          !anyListed ? (
            <ConnectedZeroDay
              containerId={containerId}
              onBrowse={() => setPicked({ kind: "all" })}
            />
          ) : (
            <div className="space-y-4">
              {connectedCount < CONNECT_TARGET && (
                <ConnectMoreNudge
                  containerId={containerId}
                  count={connectedCount}
                  onBrowse={() => setPicked({ kind: "all" })}
                />
              )}
              {!anyMatch ? (
                <p className="p-2 text-sm text-muted-foreground">
                  No connected integrations match your search.
                </p>
              ) : (
                <ConnectedLists
                  lists={filtered}
                  containerId={containerId}
                  canManage
                  onChanged={onAccountsChanged}
                />
              )}
            </div>
          )
        ) : error ? (
          <p className="p-2 text-sm text-destructive">{error}</p>
        ) : loading && apps.length === 0 ? (
          <LoadingIndicator />
        ) : apps.length === 0 ? (
          <p className="p-2 text-sm text-muted-foreground">
            {query.trim() !== ""
              ? "No integrations match your search."
              : view.kind === "category"
                ? "No integrations in this category yet."
                : "No integrations yet."}
          </p>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2 2xl:grid-cols-3">
              {apps.map((app) =>
                app.connectedBy === "app" ? (
                  connectionTile(app)
                ) : (
                  <CatalogTile
                    key={app.slug}
                    app={app}
                    connectedCount={connectedByAppCounts.get(app.slug) ?? 0}
                    busy={connecting === app.slug}
                    disabled={connecting !== null}
                    onConnect={() => void connect(app)}
                  />
                ),
              )}
            </div>
            {hasMore && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full"
                disabled={loading}
                onClick={loadMore}
              >
                {loading ? "Loading…" : "Load more"}
              </Button>
            )}
          </>
        )}
      </section>
    </div>
  );
}

// Which Boo(s) the copy is about: the tab is Boo-scoped, org settings is not.
function booSubject(containerId?: string): { subject: string; possessive: string } {
  return containerId
    ? { subject: "this Boo", possessive: "its" }
    : { subject: "your Boos", possessive: "their" };
}

// Connected view at zero: a first-run pitch rather than a bare "nothing here".
// Reuses the onboarding message — one tool alone is table stakes, the value is
// stitching a few together.
function ConnectedZeroDay({
  containerId,
  onBrowse,
}: {
  containerId?: string;
  onBrowse: () => void;
}) {
  const { subject } = booSubject(containerId);
  return (
    <div className="flex flex-col items-center gap-4 rounded-lg border border-dashed p-10 text-center">
      <div className="max-w-md space-y-1.5">
        <p className="text-sm font-medium">No integrations connected yet</p>
        <p className="text-sm text-muted-foreground">
          Each tool on its own is something you already have. Connect three or
          more and {subject} can start stitching them together into real work.
        </p>
      </div>
      <Button size="sm" onClick={onBrowse}>
        Browse integrations
      </Button>
    </div>
  );
}

// Connected view below the target: keep the momentum without nagging. One
// quiet line plus a jump back to the catalog.
function ConnectMoreNudge({
  containerId,
  count,
  onBrowse,
}: {
  containerId?: string;
  count: number;
  onBrowse: () => void;
}) {
  const { subject } = booSubject(containerId);
  const remaining = CONNECT_TARGET - count;
  return (
    <div className="flex flex-col gap-3 rounded-lg border bg-muted/30 p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="space-y-0.5">
        <p className="text-sm font-medium tabular-nums">
          {count} of {CONNECT_TARGET} connected
        </p>
        <p className="text-sm text-muted-foreground">
          {remaining === 1 ? "One more" : "Two more"} and {subject} can start
          stitching tools together. That&apos;s where the payoff shows up.
        </p>
      </div>
      <Button size="sm" variant="outline" className="sm:shrink-0" onClick={onBrowse}>
        Browse integrations
      </Button>
    </div>
  );
}

// Zapier-style rail: the synthetic Connected entry (with count) on top, and
// Custom under it when the Boo has connectors, then the hub taxonomy. Category expansion is derived from the active view: a
// parent is expanded exactly when it or one of its children is selected, so
// the rail never gets out of sync with the filter and needs no expand state.
// Sticky within the tab's scroll pane so the taxonomy stays reachable while
// the grid scrolls.
export function CategoryRail({
  tree,
  loading,
  connectedCount,
  customCount = 0,
  customWaiting = false,
  view,
  onPick,
  showConnected = true,
}: {
  tree: (CategoryInfo & { children: CategoryInfo[] })[];
  loading: boolean;
  connectedCount: number;
  // The Boo's connectors; the Custom entry shows only when there are some.
  customCount?: number;
  customWaiting?: boolean;
  view: BrowserView;
  onPick: (view: BrowserView) => void;
  // Onboarding hides the Connected entry — there, progress lives in the
  // pitch column, not the rail.
  showConnected?: boolean;
}) {
  if (loading) {
    return (
      <div className="hidden w-52 shrink-0 flex-col gap-2 pt-1 lg:flex" aria-hidden>
        {Array.from({ length: 7 }, (_, i) => (
          <div key={i} className="h-6 animate-pulse rounded-md bg-muted" />
        ))}
      </div>
    );
  }
  // Taxonomy unavailable and nothing connected or custom — drop the rail and
  // leave search as the way in. If there are connections we keep the rail so
  // Connected + All integrations stay reachable even without categories.
  if (tree.length === 0 && connectedCount === 0 && customCount === 0) return null;

  const selectedSlug = view.kind === "category" ? view.slug : null;

  return (
    <nav
      className="sticky top-0 hidden max-h-[80vh] w-52 shrink-0 flex-col gap-0.5 self-start overflow-y-auto lg:flex"
      aria-label="Integration categories"
    >
      {showConnected && (
        <>
          <RailItem
            label="Connected"
            count={connectedCount}
            active={view.kind === "connected"}
            onPick={() => onPick({ kind: "connected" })}
          />
          {customCount > 0 && (
            <RailItem
              label="Custom"
              count={customCount}
              dot={customWaiting}
              active={view.kind === "custom"}
              onPick={() => onPick({ kind: "custom" })}
            />
          )}
          <div className="my-2 border-t" />
        </>
      )}
      <RailItem
        label="All integrations"
        active={view.kind === "all"}
        onPick={() => onPick({ kind: "all" })}
      />
      {tree.map((cat) => {
        const expanded =
          selectedSlug === cat.slug || cat.children.some((k) => k.slug === selectedSlug);
        return (
          <div key={cat.slug} className="flex flex-col gap-0.5">
            <RailItem
              label={cat.name}
              active={selectedSlug === cat.slug}
              chevron={cat.children.length > 0 ? (expanded ? "open" : "closed") : undefined}
              onPick={() => onPick({ kind: "category", slug: cat.slug })}
            />
            {expanded && cat.children.length > 0 && (
              <div className="ml-3 flex flex-col gap-0.5 border-l pl-2">
                {cat.children.map((kid) => (
                  <RailItem
                    key={kid.slug}
                    label={kid.name}
                    active={selectedSlug === kid.slug}
                    onPick={() => onPick({ kind: "category", slug: kid.slug })}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}

function RailItem({
  label,
  active,
  count,
  dot,
  chevron,
  onPick,
}: {
  label: string;
  active: boolean;
  count?: number;
  // Something here waits on a person.
  dot?: boolean;
  chevron?: "open" | "closed";
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={active}
      className={cn(
        "flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-sm transition-colors",
        active
          ? "bg-muted font-medium text-foreground"
          : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
      )}
    >
      <span className="truncate">{label}</span>
      {dot && (
        <>
          <span className="size-1.5 shrink-0 rounded-full bg-amber-500" aria-hidden />
          <span className="sr-only">, something is waiting for you</span>
        </>
      )}
      {count !== undefined && (
        <span className="ml-auto text-xs tabular-nums text-muted-foreground">{count}</span>
      )}
      {chevron && (
        <ChevronRight
          className={cn(
            "ml-auto size-3.5 shrink-0 text-muted-foreground transition-transform",
            chevron === "open" && "rotate-90",
          )}
        />
      )}
    </button>
  );
}

// Compact stand-in for the rail beside the search box on small screens:
// Connected on top, then the taxonomy. Parents with children get a submenu
// (pick the parent itself via "All <name>", or a child to narrow); the hub
// expands a parent to its descendants.
export function CategoryMenu({
  connectedCount,
  customCount = 0,
  view,
  onPick,
  showConnected = true,
}: {
  connectedCount: number;
  customCount?: number;
  view: BrowserView;
  onPick: (view: BrowserView) => void;
  showConnected?: boolean;
}) {
  const { categories, loading } = useCategories();
  const tree = buildCategoryTree(categories);

  // Taxonomy unavailable — drop the control and leave search as the way in.
  if (!loading && tree.length === 0 && connectedCount === 0) return null;

  const selectedSlug = view.kind === "category" ? view.slug : null;
  const label =
    view.kind === "connected"
      ? "Connected"
      : view.kind === "custom"
        ? "Custom"
        : view.kind === "category"
          ? (categories.find((c) => c.slug === view.slug)?.name ?? view.slug)
          : null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {/* Fixed width so the search box doesn't resize as the label changes. */}
        <Button
          variant="outline"
          disabled={loading}
          className="w-full justify-between font-normal sm:w-44"
        >
          <span className={cn("truncate", label === null && "text-muted-foreground")}>
            {label ?? "All categories"}
          </span>
          <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        {showConnected && (
          <>
            <CategoryItem
              label={`Connected (${connectedCount})`}
              active={view.kind === "connected"}
              onPick={() => onPick({ kind: "connected" })}
            />
            {customCount > 0 && (
              <CategoryItem
                label={`Custom (${customCount})`}
                active={view.kind === "custom"}
                onPick={() => onPick({ kind: "custom" })}
              />
            )}
            <DropdownMenuSeparator />
          </>
        )}
        <CategoryItem
          label="All categories"
          active={view.kind === "all"}
          onPick={() => onPick({ kind: "all" })}
        />
        <DropdownMenuSeparator />
        {tree.map((cat) => {
          if (cat.children.length === 0) {
            return (
              <CategoryItem
                key={cat.slug}
                label={cat.name}
                active={selectedSlug === cat.slug}
                onPick={() => onPick({ kind: "category", slug: cat.slug })}
              />
            );
          }
          const activeHere =
            selectedSlug === cat.slug || cat.children.some((k) => k.slug === selectedSlug);
          return (
            <DropdownMenuSub key={cat.slug}>
              <DropdownMenuSubTrigger>
                {cat.name}
                {activeHere && <Check className="ml-auto" />}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-52">
                <CategoryItem
                  label={`All ${cat.name}`}
                  active={selectedSlug === cat.slug}
                  onPick={() => onPick({ kind: "category", slug: cat.slug })}
                />
                <DropdownMenuSeparator />
                {cat.children.map((kid) => (
                  <CategoryItem
                    key={kid.slug}
                    label={kid.name}
                    active={selectedSlug === kid.slug}
                    onPick={() => onPick({ kind: "category", slug: kid.slug })}
                  />
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// One row of the category menu: label, pick handler, check when active.
function CategoryItem({
  label,
  active,
  onPick,
}: {
  label: string;
  active: boolean;
  onPick: () => void;
}) {
  return (
    <DropdownMenuItem onSelect={onPick}>
      {label}
      {active && <Check className="ml-auto" />}
    </DropdownMenuItem>
  );
}

function AppLogo({ app }: { app: AppInfo }) {
  return app.logoUrl ? (
    // No image outline: hub logos are transparent PNGs, so an outline frames
    // the empty bounding box, not the glyph.
    <img src={app.logoUrl} alt="" className="size-8 shrink-0 rounded object-contain" />
  ) : (
    <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-xs font-semibold">
      {monogram(app.name)}
    </div>
  );
}

export function CatalogTile({
  app,
  connectedCount,
  busy,
  disabled,
  onConnect,
}: {
  app: AppInfo;
  connectedCount: number;
  busy: boolean;
  disabled: boolean;
  onConnect: () => void;
}) {
  return (
    // While any connect is in flight the whole grid is disabled; the active
    // tile keeps full opacity and shows the spinner, the rest dim.
    <button
      type="button"
      disabled={disabled}
      aria-busy={busy}
      onClick={onConnect}
      className={cn(
        "flex w-full items-center gap-3 rounded-lg border bg-background p-4 text-left outline-none transition-[background-color,opacity,scale] hover:bg-muted/40 active:scale-[0.96] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
        disabled && !busy && "opacity-60",
      )}
    >
      <AppLogo app={app} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold leading-tight">{app.name}</p>
        <p className="truncate text-xs text-muted-foreground">{app.description}</p>
      </div>
      {busy ? (
        <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" />
      ) : (
        connectedCount > 0 && (
          <span className="inline-flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground tabular-nums">
            <span className="size-1.5 rounded-full bg-emerald-500" />
            {connectedCount}
          </span>
        )
      )}
    </button>
  );
}
